import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@cargomatic/ui';
import AddButton from 'components/v2/buttons/AddButton';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import ToolTip from 'components/v2/Tooltip';
import { Edit, MoreHorizontal, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { unescapeHTML } from 'underscore.string';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import _ from 'lodash';
import classNames from 'classnames';

const HAZMAT_CLASSES = ['N/A', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const HAZMAT_PACKAGING_GROUP = ['N/A', 'I', 'II', 'III'];

const hazmatClassesList = _.map(HAZMAT_CLASSES, (cl) => {
  return {
    value: cl,
    localizationSlug: cl === 'N/A' ? 'N_A' : cl.toString(),
  };
});

const hazmatPackagingGroupList = _.map(HAZMAT_PACKAGING_GROUP, (pg) => {
  return {
    value: pg,
    localizationSlug: pg === 'N/A' ? 'N_A' : pg.toString(),
  };
});

const isEmptyHazmat = (hazmatDetails) => {
  const {
    hazmat_class,
    hazmat_packaging_group,
    hazmat_description = '',
  } = hazmatDetails;
  return (
    hazmat_class === 'N/A' &&
    hazmat_packaging_group === 'N/A' &&
    hazmat_description.trim() === ''
  );
};

const prepareOrderHazmatDetails = (order) => {
  if (
    featureEnabled(CONFIGURED_FEATURES.ENABLE_MULTIPLE_HAZMAT_DETAILS) &&
    order?.hazmat_details?.length > 0
  ) {
    return order.hazmat_details;
  }
  const hazmatDetails = _.pick(order, [
    'hazmat_class',
    'hazmat_packaging_group',
    'hazmat_description',
  ]);
  if (isEmptyHazmat(hazmatDetails)) return [];
  return [hazmatDetails];
};

const EditDropdown = ({ hazmatDetail, disabled = false, onSelect }) => {
  const { t } = useTranslation();
  const hazmatId = hazmatDetail._id;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          data-testid={`${hazmatId}-Dropdown-d41900dd`}
          aria-label={t('MORE_OPTIONS')}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          data-testid={`${hazmatId}-Dropdown-button-d41909dd`}
          onSelect={() => onSelect(hazmatDetail, 'edit')}
        >
          <Edit className="h-4 w-4 mr-2" />
          {t('EDIT')}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`${hazmatId}-Dropdown-button-d46900dd`}
          onSelect={() => onSelect(hazmatDetail, 'delete')}
          className="text-red-600"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          {t('DELETE')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const HazmatActionConfirmation = ({
  hazmatDetail,
  action,
  disableApprove,
  handleApprove,
  handleCancel,
}) => {
  const { t } = useTranslation();
  const hazmatId = hazmatDetail._id;
  const isEdit = action === 'edit';
  const approveTooltipLabel = isEdit
    ? t('SAVE_HAZMAT_DETAIL')
    : t('DELETE_HAZMAT_DETAIL');
  return (
    <div className="flex gap-[5px]">
      <ToolTip
        dataTestId={`${hazmatId}-CargomaticApproveTooltip-74bc9003`}
        id={`${hazmatId}_approve_button`}
        position="left"
        bodyParams={{
          dataTestId: `${hazmatId}-ApproveButton-c6f695g8`,
          onClick: () => {
            handleApprove(hazmatId);
          },
          disabled: disableApprove,
        }}
        body={({ dataTestId, onClick, disabled }) =>
          isEdit ? (
            <ApproveButton
              dataTestId={dataTestId}
              onClick={onClick}
              disabled={disabled}
            />
          ) : (
            <DeleteButton
              dataTestId={dataTestId}
              onClick={onClick}
              disabled={disabled}
            />
          )
        }
      >
        {approveTooltipLabel}
      </ToolTip>
      <ToolTip
        dataTestId={`${hazmatId}-CargomaticDeclineTooltip-09f91576`}
        id={`${hazmatId}_decline_button`}
        position="left"
        bodyParams={{
          dataTestId: `${hazmatId}-DeclineButton-09f91576`,
          onClick: () => {
            handleCancel(hazmatId);
          },
        }}
        body={({ dataTestId, onClick }) => (
          <DeclineButton onClick={onClick} dataTestId={dataTestId} />
        )}
      >
        {t('CANCEL')}
      </ToolTip>
    </div>
  );
};

const HazmatActionButton = ({
  hazmatDetail,
  hasEditAccess,
  onDropdownSelect,
}) => {
  const { t } = useTranslation();
  const hazmatId = hazmatDetail._id;
  if (!hasEditAccess) {
    return (
      <ToolTip
        dataTestId={`${hazmatId}CargomaticTooltip-74bc9003`}
        id={`${hazmatId}document_list`}
        position="left"
        bodyParams={{
          disabled: !hasEditAccess,
          onSelect: onDropdownSelect,
          hazmatDetail,
        }}
        body={EditDropdown}
      >
        {t('HAZMAT_DETAIL_INFO_TOOLTIP')}
      </ToolTip>
    );
  }
  return (
    <EditDropdown hazmatDetail={hazmatDetail} onSelect={onDropdownSelect} />
  );
};

const CreateRow = ({
  orderHazmatDetails,
  handleAddHazmatDetail,
  addDuplicateIndice,
  removeDuplicateIndice,
  duplicateIndices,
  setIsEmptyDuplication,
  isEmptyDuplication,
}) => {
  const { t } = useTranslation();
  const defaultNAValue = { value: 'N/A', localizationSlug: 'N_A' };
  const [hazmatClass, setHazmatClass] = useState(defaultNAValue);
  const [hazmatPackagingGroup, setHazmatPackagingGroup] =
    useState(defaultNAValue);
  const [hazmatDescription, setHazmatDescription] = useState('');

  const isDuplicateCombination = () => {
    return orderHazmatDetails.some((obj) => {
      const { hazmat_class, hazmat_packaging_group, hazmat_description } = obj;
      return (
        hazmatClass.value === hazmat_class &&
        hazmatPackagingGroup.value === hazmat_packaging_group &&
        hazmatDescription === hazmat_description
      );
    });
  };

  useEffect(() => {
    const isDuplicate = isDuplicateCombination();
    if (isDuplicate) {
      addDuplicateIndice(-1);
      if (
        isEmptyHazmat({
          hazmat_class: hazmatClass.value,
          hazmat_packaging_group: hazmatPackagingGroup.value,
          hazmat_description: hazmatDescription,
        })
      )
        setIsEmptyDuplication(true);
    } else {
      removeDuplicateIndice(-1);
      setIsEmptyDuplication(false);
    }
  }, [
    hazmatClass,
    hazmatDescription,
    hazmatPackagingGroup,
    orderHazmatDetails,
  ]);

  const isEmptyData = () => {
    return isEmptyHazmat({
      hazmat_class: hazmatClass.value,
      hazmat_packaging_group: hazmatPackagingGroup.value,
      hazmat_description: hazmatDescription,
    });
  };

  const handleClassSelect = (item) => {
    setHazmatClass(item);
  };

  const handlePackagingGroupSelect = (item) => {
    setHazmatPackagingGroup(item);
  };

  return (
    <TableRow
      className={classNames({
        'bg-red-50 border-red-200':
          duplicateIndices.includes(-1) && !isEmptyDuplication,
      })}
    >
      <TableCell>
        <CargomaticSelect
          dataTestId="CargomaticSelect-92dc5d28"
          showCaption
          hideLabel
          items={hazmatClassesList}
          selected={hazmatClass?.value}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={handleClassSelect}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          caption={t('SELECT_HAZMAT_CLASS')}
        />
      </TableCell>
      <TableCell>
        <CargomaticSelect
          dataTestId="CargomaticSelect-6c1cb9a8"
          showCaption
          hideLabel
          items={hazmatPackagingGroupList}
          selected={hazmatPackagingGroup?.value || ''}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={handlePackagingGroupSelect}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          caption={t('SELECT_HAZMAT_PACKAGING_GROUP')}
        />
      </TableCell>
      <TableCell>
        <CargomaticInput
          dataTestId="CargomaticInput-62f5540f"
          hideLabel
          type="text"
          name="template-name"
          className="w-full"
          placeholder={t('HAZMAT_DESCRIPTION')}
          value={hazmatDescription}
          onChange={(value) => {
            setHazmatDescription(value);
          }}
        />
      </TableCell>
      <TableCell>
        <AddButton
          dataTestId="AddButton-0ed24a4d"
          disabled={duplicateIndices.includes(-1) || isEmptyData()}
          onClick={() => {
            handleAddHazmatDetail({
              hazmatClass,
              hazmatPackagingGroup,
              hazmatDescription,
            });
            setHazmatClass(defaultNAValue);
            setHazmatPackagingGroup(defaultNAValue);
            setHazmatDescription('');
          }}
        />
      </TableCell>
    </TableRow>
  );
};

const EditRow = ({
  orderHazmatDetails,
  addDuplicateIndice,
  removeDuplicateIndice,
  duplicateIndices,
  rowIndex,
  hazmatDetail,
  handleApproveEdit,
  handleCancelEdit,
}) => {
  const { t } = useTranslation();
  const [hazmatDetailData, setHazmatDetailData] = useState(hazmatDetail);
  const hazmatId = hazmatDetail._id;

  const handleCancel = (hazmatId) => {
    removeDuplicateIndice(rowIndex);
    handleCancelEdit(hazmatId);
  };
  const emptyHazmat = isEmptyHazmat(hazmatDetailData);
  const disableApprove = emptyHazmat || duplicateIndices.includes(rowIndex);

  const buttonTooltipSlug = emptyHazmat
    ? 'CANNOT_SAVE_DATA_WITH_EMPTY_HAZMAT'
    : 'SAVE_HAZMAT_DETAIL';
  const handleEditHazmatDetail = (field, value) => {
    let newHazmatDetail = {
      [field]: value,
    };

    setHazmatDetailData((prev) => {
      return {
        ...prev,
        ...newHazmatDetail,
      };
    });
  };

  const isDuplicateRow = () => {
    const {
      hazmat_class: hazmatClass,
      hazmat_packaging_group: hazmatPackagingGroup,
      hazmat_description: hazmatDescription,
    } = hazmatDetailData;
    return orderHazmatDetails.some((obj, index) => {
      if (index === rowIndex) return false;
      const { hazmat_class, hazmat_packaging_group, hazmat_description } = obj;
      return (
        hazmatClass === hazmat_class &&
        hazmatPackagingGroup === hazmat_packaging_group &&
        hazmatDescription === hazmat_description
      );
    });
  };

  useEffect(() => {
    const isDuplicate = isDuplicateRow();
    if (isDuplicate) addDuplicateIndice(rowIndex);
    else removeDuplicateIndice(rowIndex);
  }, [hazmatDetailData, orderHazmatDetails]);

  return (
    <TableRow
      key={hazmatId}
      className={classNames({
        'bg-red-50 border-red-200': duplicateIndices.includes(rowIndex),
      })}
    >
      <TableCell className="break-words">
        <CargomaticSelect
          dataTestId={`CargomaticSelect-92dc5d28-${hazmatId}`}
          showCaption
          hideLabel
          items={hazmatClassesList}
          selected={hazmatDetailData.hazmat_class || 'N/A'}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={(selectedItem) => {
            handleEditHazmatDetail('hazmat_class', selectedItem.value);
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          caption={t('SELECT_HAZMAT_CLASS')}
        />
      </TableCell>
      <TableCell className="break-words">
        <CargomaticSelect
          dataTestId="CargomaticSelect-6c1cb9a8"
          showCaption
          hideLabel
          items={hazmatPackagingGroupList}
          selected={hazmatDetailData.hazmat_packaging_group || 'N/A'}
          className="w-full"
          inputClassNames="max-w-full min-w-full"
          onSelect={(selectedItem) => {
            handleEditHazmatDetail(
              'hazmat_packaging_group',
              selectedItem.value,
            );
          }}
          applyDefaultMargins={false}
          valueKey="value"
          textKey="localizationSlug"
          caption={t('SELECT_HAZMAT_PACKAGING_GROUP')}
        />
      </TableCell>
      <TableCell className="break-words">
        <CargomaticInput
          dataTestId="CargomaticInput-62f5540f"
          hideLabel
          type="text"
          name="template-name"
          className="w-full"
          placeholder={t('HAZMAT_DESCRIPTION')}
          value={hazmatDetailData.hazmat_description}
          onChange={(value) => {
            handleEditHazmatDetail('hazmat_description', value);
          }}
        />
      </TableCell>
      <TableCell className="break-words">
        <HazmatActionConfirmation
          hazmatDetail={hazmatDetailData}
          action="edit"
          handleCancel={handleCancel}
          handleApprove={() => handleApproveEdit(hazmatDetailData)}
          disableApprove={disableApprove}
          key={`${hazmatId}-hazmat-action`}
          buttonTooltip={t(buttonTooltipSlug)}
        />
      </TableCell>
    </TableRow>
  );
};

const ReadOnlyRow = ({
  action,
  hazmatDetail,
  hasEditAccess,
  handleApproveDelete,
  handleCancelDelete,
  onDropdownSelect,
}) => {
  const { t } = useTranslation();
  return (
    <TableRow key={hazmatDetail._id}>
      <TableCell className="break-words">
        {unescapeHTML(hazmatDetail.hazmat_class)}
      </TableCell>
      <TableCell className="break-words">
        {unescapeHTML(hazmatDetail.hazmat_packaging_group)}
      </TableCell>
      <TableCell className="break-words">
        {unescapeHTML(hazmatDetail.hazmat_description)}
      </TableCell>
      {hasEditAccess && (
        <TableCell className="break-words">
          {action === 'delete' ? (
            <HazmatActionConfirmation
              hazmatDetail={hazmatDetail}
              action="delete"
              key={`${hazmatDetail._id}-hazmat-action`}
              handleApprove={handleApproveDelete}
              handleCancel={handleCancelDelete}
              buttonTooltip={t('DELETE_HAZMAT_DETAIL')}
            />
          ) : (
            <HazmatActionButton
              hazmatDetail={hazmatDetail}
              hasEditAccess={hasEditAccess}
              onDropdownSelect={onDropdownSelect}
            />
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

const HazmatRow = ({
  orderHazmatDetails,
  addDuplicateIndice,
  removeDuplicateIndice,
  duplicateIndices,
  rowIndex,
  handleApproveDelete,
  handleUpdateHazmat,
  handleCancelEdit,
  handleCancelDelete,
  onDropdownSelect,
  hazmatDetail,
  hasEditAccess,
  editingIds,
}) => {
  const hazmatId = hazmatDetail._id;
  const action = editingIds[hazmatId];
  switch (action) {
    case 'edit':
      return (
        <EditRow
          orderHazmatDetails={orderHazmatDetails}
          addDuplicateIndice={addDuplicateIndice}
          removeDuplicateIndice={removeDuplicateIndice}
          duplicateIndices={duplicateIndices}
          handleApproveEdit={handleUpdateHazmat}
          handleCancelEdit={handleCancelEdit}
          hazmatDetail={hazmatDetail}
          rowIndex={rowIndex}
          key={`edit_row_${hazmatId}`}
        />
      );
    case 'delete':
      return (
        <ReadOnlyRow
          handleApproveDelete={handleApproveDelete}
          handleCancelDelete={handleCancelDelete}
          action={action}
          hasEditAccess={hasEditAccess}
          hazmatDetail={hazmatDetail}
          onDropdownSelect={onDropdownSelect}
          key={`readonly_row_delete`}
        />
      );
    default:
      return (
        <ReadOnlyRow
          action={action}
          hasEditAccess={hasEditAccess}
          onDropdownSelect={onDropdownSelect}
          hazmatDetail={hazmatDetail}
          key={`readonly_row_read`}
        />
      );
  }
};

function HazmatDetails({ order, updateOrderHazmatDetails, isEditable }) {
  const { t } = useTranslation();
  const [duplicateIndices, setDuplicateIndices] = useState([]);
  const [isEmptyDuplication, setIsEmptyDuplication] = useState(false);
  const orderHazmatDetails = useMemo(
    () => prepareOrderHazmatDetails(order),
    [order],
  );

  // Map of hazmat_detail_id : 'action'
  const [editingIds, setEditingIds] = useState({});

  const hasEditAccess = useMemo(() => true) && isEditable;

  const handleClearForm = (hazmatDetailId) => {
    if (hazmatDetailId) {
      return setEditingIds((prev) => {
        const updated = { ...prev };
        delete updated[hazmatDetailId];
        return updated;
      });
    }
    return setEditingIds({});
  };

  const handleAddHazmatDetail = async ({
    hazmatClass,
    hazmatPackagingGroup,
    hazmatDescription = '',
  }) => {
    const hazmat_class = hazmatClass.value || 'N/A';
    const hazmat_packaging_group = hazmatPackagingGroup.value || 'N/A';
    const hazmatDescriptionTrimmed = hazmatDescription?.trim() || '';

    const updatedOrderHazmatDetails = _.concat(orderHazmatDetails, {
      hazmat_class,
      hazmat_packaging_group,
      hazmat_description: hazmatDescriptionTrimmed,
    });
    try {
      await updateOrderHazmatDetails(order._id, updatedOrderHazmatDetails);
      // handleClearForm();
    } catch {
      showToast(t('HAZMAT_DETAIL_CREATION_FAIL'), '', 'error');
    }
  };

  const handleUpdateHazmatDetails = async (hazmatDetailData) => {
    const hazmatDetailsIndex = orderHazmatDetails.findIndex(
      (hd) => hd._id.toString() === hazmatDetailData._id,
    );
    let updatedOrderHazmatDetails = _.clone(orderHazmatDetails);
    updatedOrderHazmatDetails.splice(hazmatDetailsIndex, 1, hazmatDetailData);

    try {
      await updateOrderHazmatDetails(order._id, updatedOrderHazmatDetails);
      handleClearForm(hazmatDetailData._id);
    } catch {
      showToast(t('HAZMAT_DETAIL_UPDATION_FAIL'), '', 'error');
    }
  };

  const handleDeleteHazmatDetails = async (hazmat_id) => {
    const updatedOrderHazmatDetails = _.clone(orderHazmatDetails).filter(
      (hd) => hd._id.toString() !== hazmat_id,
    );

    try {
      await updateOrderHazmatDetails(order._id, updatedOrderHazmatDetails);
      handleClearForm(hazmat_id);
    } catch {
      showToast(t('HAZMAT_DETAIL_DELETION_FAIL'), '', 'error');
    }
  };

  const handleCancelEdit = (hazmat_id) => {
    setEditingIds((prev) => {
      const updated = { ...prev };
      delete updated[hazmat_id];
      return updated;
    });
  };

  const handleCancelDelete = (hazmat_id) => {
    setEditingIds((prev) => {
      const updated = { ...prev };
      delete updated[hazmat_id];
      return updated;
    });
  };

  const handleApproveDelete = (hazmat_id) => {
    handleDeleteHazmatDetails(hazmat_id);
  };

  const onDropdownSelect = (hazmat, action) => {
    setEditingIds((prev) => ({
      ...prev,
      [hazmat._id]: action,
    }));
  };

  const addDuplicateIndice = (index) => {
    setDuplicateIndices((prev) => {
      return [...prev, index];
    });
  };

  const removeDuplicateIndice = (index) => {
    setDuplicateIndices((prev) => {
      return prev.filter((i) => i !== index);
    });
  };

  const renderDuplicateAlert = () => {
    return (
      <Alert
        type="error"
        className="alert-error bg-red-100 text-red-900 font-medium"
        data-testid="alert"
      >
        {t('DUPLICATE_COMBINATIONS_FOUND')}
      </Alert>
    );
  };

  return (
    <CargomaticCollapsibleCard
      id="order-hazmat-details"
      header={() => t('HAZARDOUS_MATERIALS')}
    >
      <div className="overflow-x-auto">
        {Boolean(duplicateIndices.length) &&
          !isEmptyDuplication &&
          renderDuplicateAlert()}
        {!orderHazmatDetails?.length && !isEditable ? (
          <p className="py-4 sm:py-4">{t('NO_HAZMAT_DETAILS_ON_ORDER')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead
                  className="whitespace-nowrap"
                  data-testid="TableHead-f4ff494b"
                >
                  {t('HAZMAT_CLASS')}
                </TableHead>
                <TableHead
                  className="whitespace-nowrap"
                  data-testid="TableHead-f4ff494t"
                >
                  {t('HAZMAT_PACKAGING_GROUP')}
                </TableHead>
                <TableHead
                  className="whitespace-nowrap"
                  data-testid="TableHead-f4ff494r"
                >
                  {t('HAZMAT_DESCRIPTION')}
                </TableHead>
                {hasEditAccess && (
                  <TableHead
                    className="whitespace-nowrap"
                    data-testid="TableHead-f4ff494i"
                  >
                    {t('ACTION')}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {hasEditAccess && (
                <CreateRow
                  addDuplicateIndice={addDuplicateIndice}
                  removeDuplicateIndice={removeDuplicateIndice}
                  duplicateIndices={duplicateIndices}
                  orderHazmatDetails={orderHazmatDetails}
                  handleAddHazmatDetail={handleAddHazmatDetail}
                  setIsEmptyDuplication={setIsEmptyDuplication}
                  isEmptyDuplication={isEmptyDuplication}
                  key="create-row"
                />
              )}

              {orderHazmatDetails?.map((hazmatDetail, index) => (
                <HazmatRow
                  orderHazmatDetails={orderHazmatDetails}
                  addDuplicateIndice={addDuplicateIndice}
                  removeDuplicateIndice={removeDuplicateIndice}
                  duplicateIndices={duplicateIndices}
                  rowIndex={index}
                  handleApproveDelete={handleApproveDelete}
                  handleUpdateHazmat={handleUpdateHazmatDetails}
                  handleCancelEdit={handleCancelEdit}
                  handleCancelDelete={handleCancelDelete}
                  onDropdownSelect={onDropdownSelect}
                  hazmatDetail={hazmatDetail}
                  hasEditAccess={hasEditAccess}
                  editingIds={editingIds}
                  key={`hazmat_row_${index}`}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </CargomaticCollapsibleCard>
  );
}

HazmatDetails.propTypes = {
  order: PropTypes.object.isRequired,
  updateOrderHazmatDetails: PropTypes.func,
  isEditable: PropTypes.bool.isRequired,
};
HazmatDetails.defaultProps = { isEditable: false };

export default HazmatDetails;
